/**
 * Settings Component
 *
 * Manages plugin settings with form fields.
 *
 * @since 1.0.0
 */

import { ArrowPathIcon, ArrowUturnLeftIcon, CheckIcon } from '@heroicons/react/24/outline';
import { colors } from '@redfoxwp/core-components/colors';
import { Button } from '@redfoxwp/core-components/components/button';
import { Card } from '@redfoxwp/core-components/components/card';
import { Flex } from '@redfoxwp/core-components/components/flex';
import { FlexItem } from '@redfoxwp/core-components/components/flex-item';
import { LoadingSpinner } from '@redfoxwp/core-components/components/loading-spinner';
import { Modal, type ModalStrings } from '@redfoxwp/core-components/components/modal';
import { RadioGroup } from '@redfoxwp/core-components/components/radio-group';
import { Select } from '@redfoxwp/core-components/components/select';
import { SettingWrapper } from '@redfoxwp/core-components/components/setting-wrapper';
import { TabHeader } from '@redfoxwp/core-components/components/tab-header';
import { Toggle } from '@redfoxwp/core-components/components/toggle';
import { applyFilters } from '@wordpress/hooks';
import { __ } from '@wordpress/i18n';
import { useEffect, useState } from 'react';
import { useRoute } from 'wouter';
import { useHashLocation } from 'wouter/use-hash-location';

import { useSettings } from '../../contexts';
import { useNavigationGuard } from '../../hooks';
import type { SettingsData, SettingsTab } from './types';

/**
 * Settings component
 *
 * @since 1.0.0
 *
 * @return JSX element.
 */
export const Settings = (): JSX.Element => {
	// Style constants
	const containerPadding = '15px 30px';
	const inputAreaMaxWidth = '360px';

	const [, setLocation] = useHashLocation();
	const [, params] = useRoute('/settings/:tab?');
	const [showResetConfirmModal, setShowResetConfirmModal] = useState(false);

	// Get settings from context
	const {
		loading,
		saving,
		settings,
		originalSettings,
		wordpressPages,
		thankYouPages,
		error,
		updateSetting,
		saveSettings,
		resetSettings,
		resetChanges,
	} = useSettings();

	// Get unsaved changes state for browser reload warning
	const { hasUnsavedChanges } = useNavigationGuard();

	// Determine active tab from URL or default to 'thank-you-page'
	const activeTab: SettingsTab = (params?.tab as SettingsTab) || 'thank-you-page';

	// Warn user when trying to reload/close the page with unsaved changes
	useEffect(() => {
		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			if (hasUnsavedChanges) {
				event.preventDefault();
				event.returnValue = ''; // Chrome requires returnValue to be set
			}
		};

		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => window.removeEventListener('beforeunload', handleBeforeUnload);
	}, [hasUnsavedChanges]);

	// Function to navigate to a tab
	const navigateToTab = (tab: SettingsTab) => {
		setLocation(`/settings/${tab}`);
	};

	// Modal handlers
	const handleResetClick = () => {
		setShowResetConfirmModal(true);
	};

	const handleConfirmReset = async () => {
		setShowResetConfirmModal(false);
		await resetSettings();
	};

	const handleCancelReset = () => {
		setShowResetConfirmModal(false);
	};

	// Create localized strings for the modal
	const modalStrings: ModalStrings = {
		closeModal: __('Close modal', 'redfox-thank-you'),
	};

	if (loading) {
		return (
			<Flex direction="column" gap="20px" align="center" justify="center" style={{ minHeight: '400px' }}>
				<LoadingSpinner size="large" />
			</Flex>
		);
	}

	if (error && !settings) {
		return (
			<Card styleProps={{ padding: '20px' }}>
				<p>{error}</p>
			</Card>
		);
	}

	if (!settings) {
		return (
			<Card styleProps={{ padding: '20px' }}>
				<p>{__('No settings data available', 'redfox-thank-you')}</p>
			</Card>
		);
	}

	return (
		<Flex direction="column" gap="20px" fullWidth>
			<FlexItem>
				<TabHeader
					title={__('Settings', 'redfox-thank-you')}
					description={__(
						'Customize how your WooCommerce thank you pages behave and display.',
						'redfox-thank-you',
					)}
				/>
			</FlexItem>

			{/* Tab Navigation */}
			<Card styleProps={{ radius: '10px', background: '#fff', padding: '0' }} style={{ overflow: 'hidden' }}>
				<Flex direction="row" gap="0" fullWidth>
					<FlexItem
						onClick={() => navigateToTab('thank-you-page')}
						style={{
							flex: '1',
							padding: '20px 30px',
							cursor: 'pointer',
							borderBottom: activeTab === 'thank-you-page' ? `3px solid ${colors.primary[500]}` : '3px solid transparent',
							color: activeTab === 'thank-you-page' ? colors.primary[500] : colors.gray[600],
							fontWeight: activeTab === 'thank-you-page' ? 600 : 400,
							transition: 'all 0.2s ease',
							textAlign: 'center',
							fontSize: '1.1em',
						}}
					>
						{__('Thank You Page', 'redfox-thank-you')}
					</FlexItem>
					<FlexItem
						onClick={() => navigateToTab('advanced')}
						style={{
							flex: '1',
							padding: '20px 30px',
							cursor: 'pointer',
							borderBottom: activeTab === 'advanced' ? `3px solid ${colors.primary[500]}` : '3px solid transparent',
							color: activeTab === 'advanced' ? colors.primary[500] : colors.gray[600],
							fontWeight: activeTab === 'advanced' ? 600 : 400,
							transition: 'all 0.2s ease',
							textAlign: 'center',
							fontSize: '1.1em',
						}}
					>
						{__('Advanced', 'redfox-thank-you')}
					</FlexItem>
				</Flex>
			</Card>

			{/* Tab Content */}
			<Card styleProps={{ radius: '10px', background: '#fff', padding: '15px 0' }}>
				{/* Thank You Page Tab */}
				{activeTab === 'thank-you-page' && (
					<Flex direction="column" gap="0" fullWidth>
						{/* Thank You Page Type */}
						<SettingWrapper
							label={__('Thank You Page Type', 'redfox-thank-you')}
							description={__('Choose which type of thank you page to display after checkout.', 'redfox-thank-you')}
							styleProps={{
								containerPadding,
								inputAreaMaxWidth,
							}}
						>
							<RadioGroup
								name="thank_you_page_type"
								value={settings.thank_you_page_type}
								onChange={(value) =>
									updateSetting('thank_you_page_type', value)
								}
								options={applyFilters(
									'redfox_thank_you_page_type_options',
									[
										{
											value: 'default',
											label: __('Show the default WooCommerce Thank You page', 'redfox-thank-you'),
										},
										{
											value: 'custom',
											label: __('Show a custom Thank You page', 'redfox-thank-you'),
										},
									],
								) as Array<{ value: string; label: string }>}
								direction="vertical"
								size="medium"
							/>
						</SettingWrapper>

						{/* WordPress Page Selection */}
						{settings.thank_you_page_type !== 'default' && (
							<SettingWrapper
								label={__('Thank You Page to Redirect', 'redfox-thank-you')}
								description={__('Select a default thank you page to display content from.', 'redfox-thank-you')}
								htmlId="selected_thank_you_page"
								styleProps={{
									containerPadding,
									inputAreaMaxWidth,
								}}
							>
								<Select
									options={wordpressPages}
									value={String(settings.selected_thank_you_page)}
									onChange={(value) =>
										updateSetting('selected_thank_you_page', value ? Number(value) : '')
									}
									searchable={true}
									placeholder={__('Select a page...', 'redfox-thank-you')}
									styleProps={{
										width: '100%',
										borderRadius: '4px',
										borderColor: colors.gray[300],
									}}
								/>
							</SettingWrapper>
						)}

						{/* Global Thank You Page (CPT) */}
						{settings.thank_you_page_type === 'custom' && (
							<SettingWrapper
								label={__('Global Thank You Page', 'redfox-thank-you')}
								description={__('Select any published page as thank you page. Overrides default thank you page if selected.', 'redfox-thank-you')}
								htmlId="global_thank_you_page_cpt"
								styleProps={{
									containerPadding,
									inputAreaMaxWidth,
								}}
							>
								<Select
									options={thankYouPages}
									value={String(settings.global_thank_you_page_cpt)}
									onChange={(value) =>
										updateSetting('global_thank_you_page_cpt', value ? Number(value) : '')
									}
									searchable={true}
									placeholder={__('Select a page...', 'redfox-thank-you')}
									styleProps={{
										width: '100%',
										borderRadius: '4px',
										borderColor: colors.gray[300],
									}}
								/>
							</SettingWrapper>
						)}
					</Flex>
				)}

				{/* Advanced Tab */}
				{activeTab === 'advanced' && (
					<Flex direction="column" gap="0" fullWidth>
						<SettingWrapper
							label={__('Hide Page Title', 'redfox-thank-you')}
							description={__('Hide the page title on thank you pages.', 'redfox-thank-you')}
							htmlId="hide_page_title"
							styleProps={{
								containerPadding,
								inputAreaMaxWidth,
							}}
						>
							<Toggle
								id="hide_page_title"
								checked={settings.hide_page_title}
								onChange={(checked) =>
									updateSetting('hide_page_title', checked)
								}
							/>
						</SettingWrapper>

						{/* Invalid Access Redirect */}
						<SettingWrapper
							label={__('Invalid Access Redirect', 'redfox-thank-you')}
							description={__('Where to redirect users with invalid access to thank you pages.', 'redfox-thank-you')}
							htmlId="invalid_access_redirect"
							styleProps={{
								containerPadding,
								inputAreaMaxWidth,
							}}
						>
							<Select
								options={[
									{ value: 'cart', label: __('Cart Page', 'redfox-thank-you') },
									{ value: 'shop', label: __('Shop Page', 'redfox-thank-you') },
									{ value: 'home', label: __('Home Page', 'redfox-thank-you') },
									{ value: 'checkout', label: __('Checkout Page', 'redfox-thank-you') },
								]}
								value={settings.invalid_access_redirect}
								onChange={(value) =>
									updateSetting('invalid_access_redirect', value as SettingsData['invalid_access_redirect'])
								}
								searchable={false}
								placeholder={__('Select redirect page...', 'redfox-thank-you')}
								styleProps={{
									width: '100%',
									borderRadius: '4px',
									borderColor: colors.gray[300],
								}}
							/>
						</SettingWrapper>

						{/* Delete on Uninstall */}
						<SettingWrapper
							label={__('Delete plugin data on uninstall', 'redfox-thank-you')}
							description={__('Check this to delete plugin options and pages automatically on plugin uninstall.', 'redfox-thank-you')}
							htmlId="delete_on_uninstall"
							styleProps={{
								containerPadding,
								inputAreaMaxWidth,
							}}
						>
							<Toggle
								id="delete_on_uninstall"
								checked={settings.delete_on_uninstall}
								onChange={(checked) =>
									updateSetting('delete_on_uninstall', checked)
								}
							/>
						</SettingWrapper>
					</Flex>
				)}
			</Card>

			<Card styleProps={{ padding: containerPadding, radius: '10px', background: '#fff' }}>
				{/* Action Buttons */}
				<Flex direction="row" gap="12px" justify="space-between" align='center'>
					<FlexItem>
						<Button
							onClick={handleResetClick}
							disabled={saving}
							variant='outline'
							size='small'
							styleProps={{
								borderColor: colors?.gray?.[300],
								color: colors?.gray?.[500],
								hoverBackgroundColor: colors?.gray?.[100],
								hoverColor: colors?.gray?.[700],
								hoverBorderColor: colors?.gray?.[300],
							}}
						>
							<Flex direction="row" gap="8px" align="center">
								<ArrowPathIcon width={18} height={18} />
								{__('Reset to Defaults', 'redfox-thank-you')}
							</Flex>
						</Button>
					</FlexItem>
					<Flex direction="row" gap="12px">
						<FlexItem>
							<Button
								onClick={resetChanges}
								disabled={saving || !settings || JSON.stringify(settings) === JSON.stringify(originalSettings)}
								variant='outline'
							>
								<Flex direction="row" gap="8px" align="center">
									<ArrowUturnLeftIcon width={18} height={18} />
									{__('Reset Changes', 'redfox-thank-you')}
								</Flex>
							</Button>
						</FlexItem>
						<FlexItem>
							<Button
								onClick={saveSettings}
								disabled={saving}
								variant='solid'
							>
								<Flex direction="row" gap="8px" align="center">
									<CheckIcon width={18} height={18} />
									{saving ? __('Saving...', 'redfox-thank-you') : __('Save Changes', 'redfox-thank-you')}
								</Flex>
							</Button>
						</FlexItem>
					</Flex>
				</Flex>
			</Card>

			{/* Reset Confirmation Modal */}
			<Modal
				isOpen={showResetConfirmModal}
				onClose={handleCancelReset}
				title={__('Reset to Defaults', 'redfox-thank-you')}
				strings={modalStrings}
				footer={
					<Flex gap='10px' justify='flex-end'>
						<Button variant='outline' onClick={handleCancelReset}>
							{__('Cancel', 'redfox-thank-you')}
						</Button>
						<Button
							variant='solid'
							onClick={handleConfirmReset}
						>
							{__('Reset', 'redfox-thank-you')}
						</Button>
					</Flex>
				}
			>
				<p>
					{__(
						'Are you sure you want to reset all settings to their default values? This action cannot be undone.',
						'redfox-thank-you',
					)}
				</p>
			</Modal>
		</Flex>
	);
};
